import type { ReactNode } from 'react'

/**
 * Pecas compartilhadas pelos detalhes de Portfólio e Demanda.
 *
 * As duas telas usam a mesma barra de progresso (`showPort.html:22-27`,
 * `:59`) e a mesma timeline (`showPort.html:103-118`,
 * `showDemand.html:53-67`), entao o desenho mora num lugar so.
 */

/**
 * Barra de progresso derivada.
 *
 * `ratio` null significa "sem itens" — nao e 0%. Nesse caso a barra fica vazia e
 * quem chama escreve o texto explicando; a barra sozinha nunca comunica estado.
 */
export function ProgressBar({ ratio, small }: { ratio: number | null; small?: boolean }) {
  const pct = ratio === null ? 0 : Math.round(ratio * 100)
  return (
    <span
      className={small ? 'pt-bar pt-bar--sm' : 'pt-bar'}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={pct}
      aria-valuetext={ratio === null ? 'Sem Itens de Trabalho' : `${pct}% concluído`}
    >
      <span
        className={`pt-bar__fill${ratio === null ? ' pt-bar__fill--empty' : ''}`}
        style={{ width: `${pct}%` }}
      />
    </span>
  )
}

/** Rotulo textual do progresso — a porcentagem nunca vai so na barra. */
export function progressLabel(progress: { done: number; total: number; ratio: number | null }): string {
  if (progress.total === 0) return 'Sem Itens de Trabalho'
  return `${Math.round((progress.ratio ?? 0) * 100)}%`
}

export type TimelineEvent = {
  id: string
  text: ReactNode
  meta: string
  tone?: 'primary' | 'muted' | 'success'
  /** Usado so para ordenar; a formatacao vai em `meta`. */
  at: number
}

/** Timeline vertical de eventos, do mais recente para o mais antigo. */
export function Timeline({ events }: { events: readonly TimelineEvent[] }) {
  return (
    <div className="pt-timeline">
      {events.map((event) => (
        <div key={event.id} className="pt-event">
          <span className="pt-event__rail" aria-hidden>
            <span
              className={`pt-event__dot${event.tone && event.tone !== 'primary' ? ` pt-event__dot--${event.tone}` : ''}`}
            />
            <span className="pt-event__line" />
          </span>
          <span className="pt-event__body">
            <span className="pt-event__text">{event.text}</span>
            <span className="pt-event__meta">{event.meta}</span>
          </span>
        </div>
      ))}
    </div>
  )
}

const DATE_TIME = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
const DATE_ONLY = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' })

export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return '—'
  return DATE_TIME.format(typeof value === 'string' ? new Date(value) : value)
}

export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return '—'
  return DATE_ONLY.format(typeof value === 'string' ? new Date(value) : value)
}
